// English translations alongside French. Backend stores English keys natively.
import { FR } from "./i18n.jsx";

export const EN = {
  appName: "SOS MALI",
  appTagline: "Help. Anywhere. Anytime.",
  selectEmergencyHint: "Select the type of emergency",
  attachmentsHint: "Add photo, video or audio",
  secureFooter: "Your alert will be sent securely.",
  tapToSendNow: "Tap to send now",
  tapToSendInstruction: "Tap to send the alert",
  positionDetected: "Position detected",
  positionSearching: "Acquiring GPS signal…",
  navCarte: "Map",
  liveMapTitle: "Live map",
  liveMapSubtitle: "Your live location",
  cancelArmed: "Cancel",
  continueCta: "Continue",
  evidenceModalTitle: "Add evidence",
  evidenceModalHint: "Photo, video or audio (optional)",
  country: "Mali",
  navSos: "SOS",
  navContacts: "Contacts",
  navHistory: "History",
  navSettings: "Settings",

  emergencyHeader: "EMERGENCY · SOS",
  emergencyTagline: "Send an emergency alert with your live location.",
  sendAlertCta: "Send Emergency Alert",
  attachmentsTitle: "Evidence (optional)",
  chooseEmergency: "Choose emergency type",
  addPhotoVideoAudio: "Photo · Video · Audio",
  attachHint: "Photo, video, or audio recording.",
  cancel: "Cancel",
  confirm: "Confirm",
  done: "Done",
  status: "Status",
  statusReady: "Ready",
  statusLocating: "Locating",
  emergencyType: "Emergency type",
  description: "Description (optional)",
  descriptionPlaceholder: "What's happening? Injuries? Hazards? Landmarks?",
  notifyContactsTitle: "Also notify my trusted contacts",
  notifyContactsSub: "Family and friends from the Contacts tab",
  pressAndHold: "Press and hold to send the alert",
  hold: "Holding…",
  holdCta: "Hold 2.5s",
  dispatching: "Dispatching alert…",
  alertDispatched: "Alert dispatched",
  failedSend: "Failed to send the alert",
  waitingGps: "Waiting for GPS. Please enable location.",

  acquiringGps: "Acquiring GPS…",
  locationBlocked: "Location blocked",
  locationBlockedDetail: "Allow location access to send a precise position.",
  highAcc: "HIGH",
  medAcc: "MEDIUM",
  lowAcc: "LOW",
  refresh: "Refresh",

  // GPS Permission Gate
  gpsRequiredKicker: "Location required",
  gpsBlockedTitle: "Turn on location",
  gpsBlockedBody: "SOS MALI needs your location to send a precise alert to rescue services. Without GPS, emergency teams won't be able to find you quickly.",
  gpsUnavailableTitle: "GPS unavailable",
  gpsUnavailableBody: "Your device isn't getting a GPS signal. Turn on location and try outdoors or near a window.",
  gpsUnsupportedTitle: "GPS not supported",
  gpsUnsupportedBody: "This browser doesn't support geolocation. Open SOS MALI in Chrome or Safari to send a precise location.",
  gpsStepOsTitle: "On your phone",
  gpsStepOsBody: "Settings → Location → On. On Android pull down the shade and tap 'Location'. On iPhone open Settings → Privacy → Location Services.",
  gpsStepBrowserTitle: "In the browser",
  gpsStepBrowserBody: "Tap the lock icon next to the URL → Permissions → Location → Allow, then tap Retry.",
  gpsLater: "Later",
  gpsRetry: "Retry",
  gpsRetrying: "Checking…",
  close: "Close",

  // Tutorial
  tutorialKicker: "Tutorial",
  tutorialTitle: "How to send an alert",
  tutorialCtaTitle: "See how it works",
  tutorialCtaSubtitle: "Quick tutorial · 13 sec",
  tutorialClose: "Close tutorial",
  tutorialStep: "Step",
  tutorialStepOf: "of",
  tutorialDuration: "~13 sec",
  tutorialPause: "Pause",
  tutorialPlay: "Play",
  tutorialReplay: "Replay",
  tutorialGotIt: "Got it",
  tutorialStep1: "Press and hold the SOS button",
  tutorialStep2: "Choose the type of emergency",
  tutorialStep3: "Add a photo, video or audio (optional)",
  tutorialStep4: "Send the alert",
  tutorialSendLabel: "Send",
  tutorialSentPill: "Alert sent",
  tutorialPhoto: "Photo",
  tutorialAudio: "Audio",
  tutorialVideo: "Video",

  // Contacts
  contactsTitle: "CONTACTS",
  contactsKicker: "Trust",
  contactsIntro: "Family and friends notified alongside emergency services.",
  addContact: "Add a contact",
  fullName: "Full name",
  phonePh: "Phone (e.g. +22370000000)",
  emailPh: "Email",
  relPh: "Relationship (optional, e.g. Mother)",
  saving: "Saving…",
  add: "Add",
  contactCount: (n) => `${n} contact${n === 1 ? "" : "s"}`,
  noContacts: "No contacts yet. Add at least one for a faster response.",
  contactAdded: "Contact added",
  contactRemoved: "Contact removed",
  errLoadContacts: "Could not load contacts",
  errSaveContact: "Could not save contact",
  errDeleteContact: "Could not delete",
  errNameReq: "Name is required",
  errPhoneOrEmail: "Phone or email required",

  // History
  historyTitle: "HISTORY",
  historyKicker: "Log",
  historyIntro: "All alerts sent and their results.",
  records: (n) => `${n} record${n === 1 ? "" : "s"}`,
  noAlerts: "No alerts yet.",
  channels: (n) => `${n} channel${n === 1 ? "" : "s"}`,
  sentLabel: "sent",
  failedLabel: "failed",
  loading: "Loading…",

  // Settings
  settingsTitle: "SETTINGS",
  settingsKicker: "Configuration",
  settingsIntro: "Official numbers pre-filled. First recipient per emergency type.",
  dispatcherPhone: "Dispatcher phone",
  dispatcherEmail: "Dispatcher email",
  defaultsBadge: "Mali emergency numbers",
  resetDefaults: "Restore defaults",
  defaultsRestored: "Defaults restored",
  dispatcherFor: "Dispatcher · ",
  phoneShort: "Phone",
  emailShort: "Email",
  save: "Save",
  settingsSaved: "Settings saved",
  errSaveSettings: "Could not save",
  apiInfo: "To enable real SMS and email delivery, add your Twilio and Resend keys to the backend .env file. Until then, every alert is recorded and accessible via a shareable link.",

  // Result modal
  statusDelivered: "Alert delivered",
  statusPartial: "Partial delivery",
  statusInAppOnly: "Stored in app",
  type: "Type",
  deliveryReport: "Delivery report",
  openMap: "Open the map",
  shareLink: "Shareable link",
  inAppLog: "In-app log",
  channelSms: "SMS",
  channelEmail: "Email",

  // Share page
  liveAlert: "Live emergency alert",
  back: "Back",
  alertNotFound: "Alert not found",
  openPreciseLocation: "Open precise location",
  copyShareLink: "Copy link",
  linkCopied: "Link copied",
  copyFailed: "Could not copy",

  pill: {
    delivered: "delivered",
    partial: "partial",
    in_app_only: "in-app",
    dispatching: "sending",
  },
  delivery: {
    sent: "sent",
    skipped: "skipped",
    failed: "failed",
    pending: "pending",
  },

  // Offline SOS
  offlineTitle: "Offline SOS · Direct SMS",
  offlineIntro: "No internet? Send the alert by SMS via the cellular network. Tap a recipient — your SMS app opens with the message pre-filled.",
  offlineNoRecipients: "No recipient configured. Add a dispatcher in Settings or a contact.",
  sendSmsTo: "SMS to",
  dispatcher: "Dispatcher",
  smsBodyFallback: "No additional details",
  emergencyShort: "EMERGENCY",
  helpNeeded: "Help needed",
  positionLabel: "Position",
  mapLabel: "Map",
  sentVia: "Sent via SOS MALI",

  // Silent / panic mode
  silentTitle: "Silent mode",
  silentSubtitle: "Anti-violence · for victims in danger",
  silentDesc: "No on-screen confirmation. No sound. Just a short vibration. The alert is still sent and visible in History.",
  silentOn: "On",
  silentOff: "Off",
  silentBadge: "SILENT",

  // Voice recording
  recordVoice: "Record voice",
  stopRecording: "Stop",
  transcribing: "Transcribing…",
  voiceFailed: "Voice transcription failed",
  voiceUnsupported: "Voice recording is not supported here",

  // Broadcast
  broadcastTitle: "Area emergency",
  broadcastDismiss: "Got it",

  // Language
  language: "Language",
  langFR: "French",
  langEN: "English",

  // Onboarding gate
  onboardingTitle: "WELCOME",
  onboardingIntro: "Before sending alerts, identify yourself with your phone number. It is used only for alert traceability.",
  onboardingLabel: "Your phone number",
  onboardingWarning: "Any false alert may result in your number being suspended by the authorities. Please use this service responsibly.",
  onboardingShort: "Number too short",
  onboardingFailed: "Registration failed",
  onboardingSaving: "Saving…",
  onboardingContinue: "Continue",
  onboardingFooter: "Your GPS coordinates and phone number are stored securely and accessible only to administrators and emergency services.",

  // Multi-type emergency
  multiTypeHint: "You can select multiple types (e.g. major accident: Medical + Police).",
  noTypeSelected: "Select at least one emergency type",

  // Theme
  themeLabel: "Theme",
  themeLight: "Light",
  themeDark: "Dark",
  themeSystem: "Auto",

  // Intervention
  intervention: "Intervention",
  interventionPending: "Awaiting agency",
  interventionStatus: {
    dispatched: "Received",
    acknowledged: "Acknowledged",
    en_route: "En route",
    on_scene: "On scene",
    closed: "Closed",
    cancelled: "Cancelled",
  },
  agencyHandling: "Handled by",
  noAgencyYet: "No agency has taken over this alert yet.",

  // Iter93 — Planification tab (Phase 1)
  planification: "Strategy",
  planificationIndependentNote: "Workspace independent from the live-alert Carte map",
  planificationDrawingTools: "Drawing tools",
  planificationGeoLayers: "Geographic layers",
  planificationSavedProjects: "Saved projects",
  planificationComingSoon: "Available in a future release",
  planificationToolPan: "Pan",
  planificationToolZoomIn: "Zoom in",
  planificationToolZoomOut: "Zoom out",
  planificationToolPolygon: "Draw polygon",
  planificationToolLine: "Draw line",
  planificationToolMeasureDistance: "Measure distance",
  planificationToolMeasureArea: "Measure area",
  planificationToolAnnotation: "Add annotation",
  planificationToolTerrain: "Terrain / elevation",
  planificationAccessDenied: "Admins only.",
  // Phase 2B — drawing/measure tools, validation messages, mode-switch confirm.
  planificationCancelDrawing: "Cancel drawing",
  planificationClosePolygon: "Close polygon",
  planificationFinishLine: "Finish line",
  planificationTotalDistance: "Total distance",
  planificationSegmentDistance: "Segment distance",
  planificationArea: "Area",
  planificationLength: "Length",
  planificationVertex: "Vertex",
  planificationEditGeometry: "Edit geometry",
  planificationPanMap: "Pan map",
  planificationSwitchToolConfirmTitle: "Unfinished drawing",
  planificationSwitchToolConfirmMessage: "Switching tools will cancel the current drawing (not saved). Continue?",
  planificationSwitchToolConfirmOk: "Confirm",
  planificationSwitchToolConfirmCancel: "Cancel",
  planificationInvalidPolygon: "Invalid polygon",
  planificationInvalidGeometry: "Invalid geometry",
  planificationErrorNotPolygonGeojson: "Not a valid GeoJSON polygon.",
  planificationErrorInvalidCoordinates: "Invalid coordinates (out of range).",
  planificationErrorRingNotClosed: "The polygon ring is not closed.",
  planificationErrorTooFewVertices: "The polygon has fewer than three distinct vertices.",
  planificationErrorDuplicateConsecutiveVertices: "Two consecutive vertices are duplicated.",
  planificationErrorDegenerateRing: "The ring is degenerate (zero area).",
  planificationErrorSelfIntersecting: "The polygon self-intersects.",
  planificationErrorTangentContact: "A vertex touches an edge without crossing it (tangent contact).",
  planificationErrorTooFewPositions: "The line has fewer than two valid points.",
  planificationErrorNotLinestringGeojson: "Not a valid GeoJSON line.",

  // Phase 2C — annotations, selection/edit, undo/redo, object list,
  // unsaved-work protection.
  planificationAnnotationNewTitle: "New annotation",
  planificationAnnotationEditTitle: "Edit annotation",
  planificationAnnotationTitleField: "Title",
  planificationAnnotationTitleRequired: "Title is required.",
  planificationDescriptionField: "Description",
  planificationColorField: "Color",
  planificationOpacityField: "Opacity",
  planificationNameField: "Name",
  planificationSave: "Save",
  planificationCancelAction: "Cancel",
  planificationSelectAction: "Select",
  planificationEditAction: "Edit",
  planificationDeleteAction: "Delete",
  planificationMoveAction: "Move",
  planificationGpsCoordinates: "GPS coordinates",
  planificationGeoObjects: "Geographic objects",
  planificationCenterOnMap: "Center on map",
  planificationUndo: "Undo last action",
  planificationRedo: "Redo",
  planificationClearAllDrawings: "Clear all drawings",
  planificationClearAllConfirmTitle: "Confirmation required",
  planificationClearAllConfirmMessage: "Clear all drawings from this session?",
  planificationDeleteConfirmTitle: "Delete this object?",
  planificationDeleteConfirmMessage: "Delete this geographic object? You can reverse this action using Undo.",
  planificationUnsavedWork: "Unsaved work",
  planificationNoObjectsCreated: "No objects created",
  planificationUnsavedNavTitle: "Unsaved work",
  planificationUnsavedNavMessage: "You have unsaved drawings. Leaving this page will erase your work. Continue?",
  planificationStayHere: "Stay on Strategy",
  planificationLeaveAndDiscard: "Leave and discard drawings",
  planificationTypePolygon: "Polygon",
  planificationTypeLine: "Line",
  planificationTypeAnnotation: "Annotation",
  planificationTypeMeasureDistance: "Distance measurement",
  planificationTypeMeasureArea: "Area measurement",
  planificationProperties: "Properties",
  planificationNoSelection: "No object selected.",
  planificationObjectDeleted: "Object deleted.",
  planificationAllDrawingsCleared: "All drawings have been cleared.",
};

// Build a merged dictionary that falls back to French strings when a key is
// missing from EN — keeps coverage 100% even with partial translation.
const merged = (() => {
  const out = { ...FR };
  Object.keys(EN).forEach((k) => { out[k] = EN[k]; });
  return out;
})();

export const EN_FULL = merged;

export const ENGLISH_EMERGENCY = {
  Securite: "Security Forces",
  Violence: "Violence",
  Accident: "Accident",
  Medical: "Medical Emergency",
  Fire: "Fire",
  Flood: "Flood",
  Missing: "Missing Person",
  Report: "Report",
  // legacy fallbacks
  Police: "Police",
  FAMA: "FAMA",
  Security: "Security",
  Kidnapping: "Kidnapping",
  Terrorist: "Terrorism",
  Corruption: "Corruption",
  Other: "Other",
};
